import * as React from "react";
import { ActionDispatch } from "react";
import { NetworkAssessmentAnalysisType, ProjectionProviderType } from "@app/projects/enumerators";
import ProjectService from "@app/projects/projectService";
import UpdateProjectionProvider from "@app/projects/results/projection-provider/updateProjectionProvider";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import ProjectionProviderHistoryDisplay from "@app/projects/projection-provider/ProjectionProviderHistoryDisplay";
import { FlyoutContextOutgoingProps, FlyoutFooter, withFlyoutContext } from "@app/shared/Flyouts";
import Modal from "@app/shared/Modals/Modal";
import { AccessPointForProjection, CrossSpecialtyAndNetworkType, ProviderExclusionReason } from "@app/projects/results/projection-provider";
import NetNewServicingProviderService from "@app/projects/servicing-provider/NetNewServicingProviderService";
import NetNewProviderHistoryDisplay from "@app/projects/net-new-provider/NetNewProviderHistoryDisplay";
import {
  ProjectionProviderDetailsAction,
  ProjectionProviderDetailsActionType,
  ProjectionProviderDetailsForm,
  ProjectionProviderIdentifier,
  ProjectionProviderStatus
} from "@app/projects/projection-provider/projectionProviderDetailsReducer";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { Link } from "react-router-dom";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { FeatureFlagContextProps, withFeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { AdvancedExclusionsWizardRouteState, ExclusionVerifiedSources } from "@app/projects/projection-provider/advanced-exclusions/interfaces";
import { BulkProjectTypeEnum } from "@app/shared/enums/bulkProjectTypeEnum";
import { PhoneNumberFormatHelper } from "@app/projects/projection-provider/PhoneNumberFormatHelper";
import ProjectionProvider from "@app/projects/results/projection-provider/projectionProvider";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import NewActionMenu from "@app/shared/action-menu/NewActionMenu";
import { NewActionMenuItem } from "@app/shared/action-menu/NewActionMenuItem";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextArea } from "@app/shared/qes-standards/forms/TextArea";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { UserContextProps, withUserContext } from "@app/user/UserContext";

export enum ProjectionPanelMode {
  Create = "create",
  Edit = "edit",
  View = "View",
}

interface ProjectionProviderDetailsProps {
  goBack?: () => void;
  accessPoints: AccessPointForProjection[];
  crossSpecialtyAndNetworkType?: CrossSpecialtyAndNetworkType;
  hasProvidersWithoutNPI: boolean;
  networkAssessmentAnalysisType: NetworkAssessmentAnalysisType;
  priorProjectionProviderProjectIds?: number[];
  priorProjectionProviderType?: ProjectionProviderType;
  projectId: number;
  projectionPanelMode: ProjectionPanelMode;
  /** The existing projection provider for Edit/View mode. */
  projectionProvider?: ProjectionProvider;
  projectionProviderType: ProjectionProviderType;
  providerNetworkType: ProviderNetworkTypeEnum;
  /** Called after successful creation of projection providers */
  onProjectionProvidersCreated?: (runProjection: boolean, hasUnprocessedProviders: boolean | undefined) => void;
  onProjectionProviderUpdated?: (runProjection: boolean, hasUnprocessedProviders: boolean | undefined) => void;
  /** The specialty group code of the specialty group that these providers belong to */
  specialtyGroupCode: string;
  formState: ProjectionProviderDetailsForm;
  formDispatch: ActionDispatch<[action: ProjectionProviderDetailsAction]>;
  isZipProject: boolean;
  showAffiliationsInsights: boolean;
  isRunning: boolean;
  hideProviderInfoSection?: boolean;
  hideAllLocationsOption?: boolean;
  hideAllSpecialtiesOption?: boolean;
  hideAllProjectsOption?: boolean;
  showHistoryInViewMode?: boolean;
  isRegulatorProject?: boolean;
  regulatorAssignmentClient?: string;
}

interface ProjectionProviderDetailsState {
  allLocations: boolean;
  allProjects: boolean;
  allSpecialties: boolean;
  reasonsForExcluding: ProviderExclusionReason[];
  reasonsForTargeting: string[];
  regulatorSubmitAttempted: boolean;
  runProjectionOnSubmit: boolean;
  showRegulatorWarningModal: boolean;
}

const getDefaultIdentifier = (accessPoints: AccessPointForProjection[], projected: ProjectionProvider,
  crossSpecialtyAndNetworkType?: CrossSpecialtyAndNetworkType
): ProjectionProviderIdentifier => {
    // If projectionBy is explicitly set via crossSpecialtyAndNetworkType, use that
  if (crossSpecialtyAndNetworkType?.projectionBy) {
    return crossSpecialtyAndNetworkType.projectionBy;
  }
  
  if (projected) {
    if (projected.projectedByNPI && projected.projectedByTIN) {
      return ProjectionProviderIdentifier.NPITIN;
    }
    if (projected.projectedByTIN) {
      return ProjectionProviderIdentifier.TIN;
    }
    if (projected.projectedByNPI) {
      return ProjectionProviderIdentifier.NPI;
    }
    if (projected.projectedByContractEntity) {
      return ProjectionProviderIdentifier.ContractEntity;
    }
    if (projected.projectedByNPI2) {
      return ProjectionProviderIdentifier.NPI2;
    }
    if (projected.projectedByCCN) {
      return ProjectionProviderIdentifier.CCN;
    }
  }

  if (accessPoints.findIndex((provider) => !!provider.npi) > -1) {
    return ProjectionProviderIdentifier.NPI;
  }
  if (accessPoints.findIndex((provider) => !!provider.tin) > -1) {
    return ProjectionProviderIdentifier.TIN;
  }

  return ProjectionProviderIdentifier.None;
};

const getDisabledTooltipText = (isTinViewer: boolean): string => {
  return `All providers must have an NPI ${isTinViewer ? "or TIN" : ""} to target/exclude by All Locations, All Projects, and All Specialties.`;
};

type ProjectionProviderProps =
  ProjectionProviderDetailsProps
  & UserContextProps
  & FlyoutContextOutgoingProps
  & FeatureFlagContextProps;

export class ProjectionProviderDetails extends React.Component<ProjectionProviderProps, ProjectionProviderDetailsState> {
  private readonly projectService = new ProjectService();
  private readonly netNewServicingProviderService = new NetNewServicingProviderService();
  private readonly phoneNumberFormatHelper = new PhoneNumberFormatHelper();

  constructor(props: ProjectionProviderProps) {
    super(props);

    const selectedOptions = this.props.projectionProvider?.exclusionVerifiedSources || [];
    const phone = this.props.projectionProvider?.hasProjectionPhone ? this.props.projectionProvider?.phone : "";

    this.props.formDispatch({
      payload: {
        allLocations: this.props.crossSpecialtyAndNetworkType?.allLocations ?? this.props.projectionProvider?.allLocations ?? false,
        allProjects: this.props.crossSpecialtyAndNetworkType?.allProjects ?? this.props.projectionProvider?.allProjects ?? false,
        allSpecialties: this.props.crossSpecialtyAndNetworkType?.allSpecialties ?? this.props.projectionProvider?.allSpecialties ?? false,
        disableRerunPreview: true,
        hasChanged: false,
        identifier: getDefaultIdentifier(this.props.accessPoints, this.props.projectionProvider, this.props.crossSpecialtyAndNetworkType),
        isPhoneInfoIconHovered: false,
        maxLength: 1000,
        phone: this.phoneNumberFormatHelper.getNumbersFromString(phone),
        reasonDescription: this.props.projectionProvider ? this.props.projectionProvider.reasonDescription : "",
        reasonName: this.props.projectionProvider ? this.props.projectionProvider.reasonName : "",
        requireReRun: false,
        selectedExclusionVerifiedSources: selectedOptions,
        status: ProjectionProviderStatus.Ready,
      }, type: ProjectionProviderDetailsActionType.Initialize
    });

    this.state = {
      allLocations: this.props.crossSpecialtyAndNetworkType?.allLocations ?? this.props.projectionProvider?.allLocations ?? false,
      allProjects: this.props.crossSpecialtyAndNetworkType?.allProjects ?? this.props.projectionProvider?.allProjects ?? false,
      allSpecialties: this.props.crossSpecialtyAndNetworkType?.allSpecialties ?? this.props.projectionProvider?.allSpecialties ?? false,
      reasonsForExcluding: [],
      reasonsForTargeting: [],
      regulatorSubmitAttempted: false,
      runProjectionOnSubmit: true,
      showRegulatorWarningModal: false,
    };
  }

  public async componentDidMount() {
    if (this.props.projectionProviderType === ProjectionProviderType.ExcludeImpact && this.props.user.roles.indexOf(Roles.PROVIDER_EXCLUDE_USER) > -1) {
      const reasonsForExcluding = await this.projectService.getProviderExclusionReasons(this.props.projectId);
      this.setState({ reasonsForExcluding });
    }

    if (this.props.projectionProviderType === ProjectionProviderType.Target && this.props.user.roles.indexOf(Roles.PROVIDER_TARGET_USER) > -1) {
      const reasonsForTargeting = await this.projectService.getTargetProviderReasons(this.props.projectId);
      this.setState({ reasonsForTargeting });
    }
  }

  public render() {
    const createMode = this.props.projectionPanelMode === ProjectionPanelMode.Create;
    const editMode = this.props.projectionPanelMode === ProjectionPanelMode.Edit;
    const viewMode = this.props.projectionPanelMode === ProjectionPanelMode.View;
    const disabled = !this.state || (this.props.formState.status !== ProjectionProviderStatus.Ready && this.props.formState.status !== ProjectionProviderStatus.CanSubmit) || viewMode || this.props.isRunning;

    const showProgress = this.props.formState.status !== ProjectionProviderStatus.Ready && this.props.formState.status !== ProjectionProviderStatus.CanSubmit;
    const hasProvidersWithoutNPI = this.props.hasProvidersWithoutNPI;
    const disabledByIdentifier = this.props.formState.identifier === ProjectionProviderIdentifier.ContractEntity
      || this.props.formState.identifier === ProjectionProviderIdentifier.NPI2
      || this.props.formState.identifier === ProjectionProviderIdentifier.CCN;

    const tinViewer = this.props.user.roles.indexOf(Roles.TIN_VIEWER) > -1;
  
    const isRegulatorExclusionFlyout = createMode
      && this.isProviderType(ProjectionProviderType.ExcludeImpact)
      && !!this.props.isRegulatorProject
      && this.props.enabledFeatureFlags.has(Features.REGULATOR_EXCLUSION_MANAGEMENT_DASHBOARD);
    
    // Disable checkboxes if matching by name is enabled and there are any selected providers without an NPI or TIN (if applicable).
    const matchOnName = this.props.accessPoints.some((provider) => !provider.npi && (!tinViewer || !provider.tin))

    //filter out providers without an NPI AND TIN when determining what recording options to display
    const providersWithTinOrNPI = this.props.accessPoints.filter((provider) => !!provider.npi || !!provider.tin);

    const showNpi = editMode ? this.props.projectionProvider.npi != null : providersWithTinOrNPI.length > 0 && providersWithTinOrNPI.every((provider) => !!provider.npi);
    const showTin = tinViewer && editMode ? this.props.projectionProvider.tin != null : providersWithTinOrNPI.length > 0 && providersWithTinOrNPI.every((provider) => !!provider.tin);
    const showNpi2 = this.props.formState.identifier === "npi2";
    const showAffiliationsInsights = this.props.showAffiliationsInsights && !this.props.isZipProject;
    const disableContractEntityRadioButton = !this.props.accessPoints.some((provider) => provider.professionalAffiliationId > 0);
    const checkboxDisabled = viewMode || editMode;

    const cciioEnabled = this.props.enabledFeatureFlags.has(Features.CCIIO_JUSTIFICATION_EXPORT);

    const hideApplyToSectionLabel = this.props.hideAllLocationsOption && this.props.hideAllSpecialtiesOption && !((this.isProviderType(ProjectionProviderType.ExcludeImpact) || null) && !this.props.hideAllProjectsOption);
    return (
      <>
        {this.props.isRunning && (
          <QESAlert type="warning">
            This fly-out is in read-only mode while excluded or targeted providers are being processed.
          </QESAlert>
        )}

        

        {this.showWarningBanner() &&
          (<QESAlert type="warning" persist={true}>
            <div>
              This status will require additional supporting documentation to be included with CMS exception documents.
            </div>
          </QESAlert>)}

        {createMode && (<>
          {this.props.projectionProviderType == ProjectionProviderType.ExcludeImpact && this.props.enabledFeatureFlags.has(Features.LOB_EXCLUSIONS) && this.props.user.roles.includes(Roles.PROVIDER_EXCLUDE_USER) && (!this.props.isZipProject || this.props.enabledFeatureFlags.has(Features.ZIP_CONFIGURABLE_EXCLUSIONS)) &&
            <>
              <div className="flyout-wizard-link">
                <Link<AdvancedExclusionsWizardRouteState>
                  to={{
                    pathname: `/projects/${this.props.projectId}/advanced-exclusions`,
                    state: {
                      currentProjectId: this.props.projectId.toString(),
                      projectType: this.props.isZipProject ? BulkProjectTypeEnum.Zip : BulkProjectTypeEnum.County,
                      providerNetworkType: this.props.providerNetworkType,
                      selectedExclusionProviders: this.props.accessPoints,
                      specialtyGroup: this.props.specialtyGroupCode
                    }
                  }}
                  type="button"
                  className="qes-button qes-button-secondary mt-1 mr5"
                data-testid="projection_provider_details__advanced_exclusions">Configurable Exclusions</Link>
              </div>
              <hr />
            </>
          }

          {/* Progress messages */}
          <div>
            {(showProgress || null) && (
              <>
                {(this.props.formState.status === ProjectionProviderStatus.Creating || null) && (
                  <QESAlert type="info" persist={true}><i className="fa fa-spinner fa-spin" /> Processing
                    request...</QESAlert>
                )}
                {(this.props.formState.status === ProjectionProviderStatus.Finished || null) && (
                  <QESAlert type="success" persist={true}>Success! Analysis complete.</QESAlert>
                )}
                {(this.props.formState.status === ProjectionProviderStatus.Error && createMode) && (
                  <QESAlert type="danger" persist={true}>Failed
                    to {this.projectionTypeInfinitiveTense.toLowerCase()} the provider(s). Review your selections and
                    try again.</QESAlert>
                )}
                {(this.props.formState.status === ProjectionProviderStatus.Error && editMode) && (
                  <QESAlert type="danger">Failed to update. Review your selections and try again.</QESAlert>
                )}
              </>
            )}
          </div>

          {!this.props.hideProviderInfoSection &&
            <p>
              Please enter the additional information to
              <strong>
                {` ${this.projectionTypeInfinitiveTense.toLowerCase()}`}
                {` ${this.props.accessPoints.length}`}
                {` provider${this.props.accessPoints.length > 1 ? "s" : ""} `}
              </strong>
              from the
              <strong>
                {` ${this.providerNetworkTypeDisplay.toLowerCase()} `}
              </strong>
              network...
            </p>
          }
        </>)}

        {editMode && (
          <p>Edit {this.props.projectionProviderType === ProjectionProviderType.ExcludeImpact ? "Excluded" : "Targeted"} Provider
            Information</p>
        )}

        {viewMode && (
          <p>View {this.props.projectionProviderType === ProjectionProviderType.ExcludeImpact ? "Excluded" : "Targeted"} Provider
            Information</p>
        )}

        {this.props.projectionProviderType === ProjectionProviderType.ExcludeImpact && (
          <div>
            <p className="qes-text-input__label">Exclusion Verified Sources</p>
            <div className="container">
              <div className="row row-cols-2">
                {ExclusionVerifiedSources.verifiedSources.map((source) => (
                  <QESCheckbox
                    key={source}
                    label={source}
                    checked={this.props.formState.selectedExclusionVerifiedSources.includes(source)}
                    disabled={disabled}
                    onChange={(e) => {
                      const newSelectedSources = e.target.checked
                        ? [...this.props.formState.selectedExclusionVerifiedSources, source]
                        : this.props.formState.selectedExclusionVerifiedSources.filter(s => s !== source);
                      this.props.formDispatch({
                        payload: newSelectedSources,
                        type: ProjectionProviderDetailsActionType.SelectedExclusionVerifiedSources
                      });
                    }}
                    testId={`projection-flyout__exclusion-source--${source}`}
                  />
                ))}
              </div>
            </div>
            <hr />
          </div>
        )}

        {(this.props.providerNetworkType != ProviderNetworkTypeEnum.NetNew) && (
          <div className="mb-3">
            {!hideApplyToSectionLabel &&
              <p className="qes-text-input__label">Apply {this.projectionTypeForNoun} to:</p>
            }
            {!this.props.hideAllLocationsOption && (
              <div className="row col-4">
                <TooltipWrapper position="right"
                  text={this.props.formState.phone?.length > 0 ? "'All Locations' check box disabled when Phone Number is entered."
                    : (matchOnName ? getDisabledTooltipText(tinViewer) : null)}>
                  <QESCheckbox
                    label="All Locations"
                    checked={this.state.allLocations}
                    disabled={disabled || hasProvidersWithoutNPI === true || matchOnName === true || disabledByIdentifier || this.props.formState.phone?.length > 0 || !!this.props.crossSpecialtyAndNetworkType?.allLocations}
                    onChange={(e) => {
                      this.setState({ ...this.state, allLocations: e.target.checked })
                      this.props.formDispatch({
                        payload: e.target.checked,
                        type: ProjectionProviderDetailsActionType.AllLocations
                    })}}
                    testId="projection-flyout__all-locations"
                  />
                </TooltipWrapper>
              </div>
            )}

            {(this.isProviderType(ProjectionProviderType.ExcludeImpact) || null) && !this.props.hideAllProjectsOption && (
              <div className="row col-4">
                <TooltipWrapper text={matchOnName ? getDisabledTooltipText(tinViewer) : null}>
                  <QESCheckbox
                    checked={this.state.allProjects}
                    label="All Projects"
                    disabled={disabled || hasProvidersWithoutNPI === true || matchOnName === true || !!this.props.crossSpecialtyAndNetworkType?.allProjects}
                    onChange={(e) => {
                      this.setState({ ...this.state, allProjects: e.target.checked })
                      this.props.formDispatch({
                      payload: e.target.checked,
                      type: ProjectionProviderDetailsActionType.AllProjects
                    })}}
                    testId="projection-flyout__all-projects"
                  />
                </TooltipWrapper>
              </div>
            )}

            {!this.props.hideAllSpecialtiesOption && (
              <div className="row col-4">
                <TooltipWrapper text={matchOnName ? getDisabledTooltipText(tinViewer) : null}>
                  <QESCheckbox
                    label="All Specialties"
                    checked={this.state.allSpecialties}
                    disabled={disabled || hasProvidersWithoutNPI === true || matchOnName === true || disabledByIdentifier || !!this.props.crossSpecialtyAndNetworkType?.allSpecialties}
                    onChange={(e) => {
                      this.setState({ ...this.state, allSpecialties: e.target.checked })
                      this.props.formDispatch({
                      payload: e.target.checked,
                      type: ProjectionProviderDetailsActionType.AllSpecialties
                    })}}
                    testId="projection-flyout__all-specialties"
                  />
                </TooltipWrapper>
              </div>
            )}
          </div>
        )}

        {(showNpi || showTin || showNpi2 || showAffiliationsInsights) && (!this.props.crossSpecialtyAndNetworkType?.projectionBy) &&
          <div className="mb-3">
            <div className="row">
              <label className="col-12 label-s d-block mb-2" htmlFor="reason">{"Recording Criteria"}:</label>
            </div>
            <div className="row">
              <div className="col-12 ms-4">
                {showNpi && (<div>
                  <QESRadioButton
                    label={"NPI"}
                    checked={this.props.formState.identifier === "npi"}
                    disabled={checkboxDisabled}
                    name="identifier"
                    onChange={(e) => {
                      if(e.target.checked)
                      {
                        this.setState({ ...this.state, allLocations: false, allSpecialties: false })
                      }
                      this.props.formDispatch({
                      payload: e.target.value as ProjectionProviderIdentifier,
                      type: ProjectionProviderDetailsActionType.Identifier
                    })}}
                    value="npi"
                    testId="termination-flyout__by-npi"
                  />
                </div>)}

                {showTin && (<div>
                  <QESRadioButton
                    label={"TIN"}
                    checked={this.props.formState.identifier === "tin"}
                    disabled={checkboxDisabled}
                    name="identifier"
                    onChange={(e) => this.props.formDispatch({
                      payload: e.target.value as ProjectionProviderIdentifier,
                      type: ProjectionProviderDetailsActionType.Identifier
                    })}
                    value="tin"
                    testId="termination-flyout__by-tin"
                  />
                </div>)}

                {showNpi && showTin && (<div>
                  <QESRadioButton
                    label={"NPI and TIN"}
                    checked={this.props.formState.identifier === "npitin"}
                    disabled={checkboxDisabled}
                    name="identifier"
                    onChange={(e) => this.props.formDispatch({
                      payload: e.target.value as ProjectionProviderIdentifier,
                      type: ProjectionProviderDetailsActionType.Identifier
                    })}
                    value="npitin"
                    testId="termination-flyout__by-npi-and-tin"
                  />
                </div>)}

                {showNpi2 && (<div>
                  <QESRadioButton
                    label={"NPI (Type 2)"}
                    checked={this.props.formState.identifier === "npi2"}
                    disabled={checkboxDisabled}
                    name="identifier"
                    onChange={(e) => this.props.formDispatch({
                      payload: e.target.value as ProjectionProviderIdentifier,
                      type: ProjectionProviderDetailsActionType.Identifier
                    })}
                    value="npi2"
                    testId="projection-flyout_by-npi2"
                  />
                </div>)}
                {(showAffiliationsInsights || (editMode && this.props.projectionProvider?.projectedByContractEntity)) &&
                  ((this.isProviderType(ProjectionProviderType.ExcludeImpact))
                    || this.isProviderType(ProjectionProviderType.Target) || this.isProviderType(ProjectionProviderType.Unknown)) && (
                  <div className="d-flex align-items-center" >
                      <QESRadioButton
                        label="Contract Entity"
                        checked={this.props.formState.identifier === "contractentity"}
                        disabled={checkboxDisabled || disableContractEntityRadioButton}
                        radioContainerClassName={"me-2"}
                        name="identifier"
                        onChange={(e) => {
                          if(e.target.checked)
                          {
                            this.setState({ ...this.state, allLocations: true, allSpecialties: true })
                          }
                          this.props.formDispatch({
                            payload: e.target.value as ProjectionProviderIdentifier,
                            type: ProjectionProviderDetailsActionType.ContractEntity
                          });
                        }}
                        value="contractentity"
                        testId="termination-flyout__by-contract-entity"
                      />
                      <TooltipWrapper
                        text={`Selecting this will ${this.isProviderType(ProjectionProviderType.Target) ? "target" : "exclude"} all providers associated with the selected contract entities.`}
                      >
                        <QesIcon className={"mb-2"} iconName={QesIcons.InfoCircle} description="Tooltip" />
                      </TooltipWrapper>
                    </div>
                  )}
              </div>
            </div>
          </div>
        }

        <RoleRestricted allowedRoles={[Roles.PROVIDER_EXCLUDE_USER]}>
          <div className="row mb-3 mt-4">
            <TooltipWrapper containerClassName="col-6" style={{ display: "flex", flexDirection: "column", padding: "0" }}
              text={!this.props.formState.isPhoneInfoIconHovered && this.props.formState.allLocations ? "Phone Number field disabled when 'All Locations' is selected." : null}>
              {this.isProviderType(ProjectionProviderType.ExcludeImpact) &&
                <QESTextInput
                  containerClassName={!this.props.formState.isPhoneInfoIconHovered && this.props.formState.allLocations ? "col-12" : "col-6"}
                  label={this.getPhoneLabelWithTooltip()}
                  placeholder="Phone Number"
                  errorText="Phone must have 10 digits."
                  testId="projection-flyout__phone"
                  disabled={disabled || (!editMode && this.props.accessPoints.length !== 1 || this.props.formState.allLocations)}
                  value={this.phoneNumberFormatHelper.formatPhone(this.props.formState.phone)}
                  unrequiredValidation={true}
                  isValid={this.props.formState.phone?.length === 0 || this.props.formState.phone?.length === 10}
                  onChange={e => this.props.formDispatch({
                    payload: this.phoneNumberFormatHelper.getNumbersFromString(e.target.value),
                    type: ProjectionProviderDetailsActionType.Phone
                  })}
                />
              }
            </TooltipWrapper>
          </div>
        </RoleRestricted>

        <div className="row mb-3 col-6">
          <QESSelect
            label={"Reason"}
            disabled={disabled}
            testId="projection-flyout__reason"
            containerClassName="projection-flyout__reason"
            onChange={e => this.props.formDispatch({
              payload: e.target.value,
              type: ProjectionProviderDetailsActionType.ReasonName
            })}
            value={this.props.formState.reasonName}
            required={true}
            isValid={!!this.props.formState.reasonName}
            placeholder={`Reason for ${this.projectionTypePresentTense.toLowerCase()}`}
          >
            {viewMode ? (
              <option value={this.props.formState.reasonName}>{this.props.formState.reasonName}</option>
            ) : (
              <>
                {this.isProviderType(ProjectionProviderType.Target) && this.state.reasonsForTargeting.map((o, i) => (
                  <option key={i + 1} value={o}>{o}</option>
                ))}
                {this.isProviderType(ProjectionProviderType.ExcludeImpact) && this.state.reasonsForExcluding.map((o, i) => {
                  const maSuffix = o.isFacilityBasedISNP ? '**' : o.validReasonForMAExceptionRequestForm ? '*' : '';
                  const cciioSuffix = cciioEnabled && o.validReasonForCCIIOJustificationExtract ? '^' : '';
                  return (
                    <option key={i + 1} value={o.reason}>
                      {`${o.reason}${maSuffix}${cciioSuffix}`}
                    </option>
                  );
                })}
              </>
            )}
          </QESSelect>
          {this.isProviderType(ProjectionProviderType.ExcludeImpact) && (
            <>
              <p className="mt-1">* Designates a valid reason for MA Exception Request form. Invalid reasons will appear blank on CMS Exception Request form.</p>
              <p className="mt-1">** Only facility-based I-SNPs may use as a valid reason.</p>
              {cciioEnabled && (
                <p className="mt-1">^ Designates a valid reason to select for the CCIIO Justification Form extract. Invalid reasons will appear blank in the data extract.</p>
              )}
            </>)}
        </div>

        <div className="row mb-3">
          <QESTextArea
            containerClassName="col-6"
            disabled={disabled}
            onChange={e => this.props.formDispatch({
              payload: e.target.value,
              type: ProjectionProviderDetailsActionType.ReasonDescription
            })}
            value={this.props.formState.reasonDescription}
            maxLength={this.props.formState.maxLength}
            label="Comment"
            testId="projection-flyout__comment"
            required={this.props.formState.reasonName === "Other" || (isRegulatorExclusionFlyout && this.state.regulatorSubmitAttempted)}
            isValid={!!this.props.formState.reasonDescription?.length}
            {...(isRegulatorExclusionFlyout && this.state.regulatorSubmitAttempted ? {
              errorText: "A comment is required when submitting to the Regulator.",
              wasFocusedExternally: !this.props.formState.reasonDescription?.length,
            } : {})}
            placeholder={isRegulatorExclusionFlyout ? "Provide supporting detail for the Regulator's review" : undefined}
          />
        </div>


        <RoleRestricted allowedRoles={[Roles.PROJECTION_PROCESSOR]}>
          <div className="row mb-3">
            {(createMode || (editMode && this.props.formState.requireReRun)) &&
              (
                <div>
                  <QESCheckbox
                    label="Run Projection Analysis Later"
                    testId="projection-flyout__run-later"
                    checked={!this.state.runProjectionOnSubmit}
                    disabled={disabled}
                    onChange={(e) => {
                      this.setState({ runProjectionOnSubmit: !e.currentTarget.checked });
                    }}
                  />
                </div>
              )
            }
          </div>
        </RoleRestricted>

        {(editMode || (viewMode && this.props.showHistoryInViewMode)) &&
          <AccordionContainer>
            <Accordion
              title="History"
              testId="projection-provider-detail__history-accordion"
            >
              {this.props.providerNetworkType == ProviderNetworkTypeEnum.NetNew ?
                <NetNewProviderHistoryDisplay netNewProviderProjectId={this.props.projectionProvider.providerId} /> :
                <ProjectionProviderHistoryDisplay
                  projectionProviderProjectId={this.props.projectionProvider.projectionProviderProjectId}
                  projectionProviderSettingsId={this.props.projectionProvider.projectionProviderSettingsId} />}
            </Accordion>
          </AccordionContainer>
        }

        <FlyoutFooter
          submitButton={
            <div>
              {this.props.projectionProviderType === ProjectionProviderType.Target && !!this.props.goBack && (
                <QESButton className="me-3" qesType="secondary-danger" onClick={this.props.goBack}
                  qesTestId="project-provider-details__go-back">Previous</QESButton>
              )}
              {isRegulatorExclusionFlyout ? (
                <NewActionMenu
                  alignRight={true}
                  buttonText="Submit"
                  buttonClassNames="qes-button-primary"
                  disabled={!this.canSubmit()}
                  dropup={true}
                  testId="projections-flyout__submit"
                >
                  <NewActionMenuItem
                    display="Internal Only"
                    isSelected={false}
                    onClick={this.handleInternalOnlySubmit}
                    testId="projections-flyout__submit-internal-only"
                  />
                  <NewActionMenuItem
                    display="Internal and Submit to Regulator"
                    isSelected={false}
                    onClick={this.handleRegulatorSubmitAttempt}
                    testId="projections-flyout__submit-regulator"
                  />
                </NewActionMenu>
              ) : (
                <QESButton disabled={!this.canSubmit()} onClick={this.executeSubmit} qesTestId="projections-flyout__submit"
                  qesType="primary">Submit</QESButton>
              )}
            </div>
          }
        />

        {isRegulatorExclusionFlyout && (
          <Modal
            isVisible={this.state.showRegulatorWarningModal}
            closeModal={() => this.setState({ showRegulatorWarningModal: false })}
            content={{
              body: (
                <p>
                  All exclusion details — including reason, verified sources, and comment — will be visible to{" "}
                  <strong>{this.props.regulatorAssignmentClient}</strong>. Are you sure you want to proceed?
                </p>
              ),
              cancelButtonText: "Cancel",
              confirmButtonAction: this.handleRegulatorWarningConfirm,
              confirmButtonText: "Submit to Regulator",
              title: "Submit to Regulator",
            }}
          />
        )}
      </>
    );
  }

  private getPhoneLabelWithTooltip() {
    return <>
      <div
        onMouseEnter={() => this.props.formDispatch({ payload: true, type: ProjectionProviderDetailsActionType.PhoneTooltip })}
        onMouseLeave={() => this.props.formDispatch({ payload: false, type: ProjectionProviderDetailsActionType.PhoneTooltip })}
      >
        Phone Number&nbsp;
        <TooltipWrapper position="right" tooltipWidth="500px"
          text={this.phoneNumberFormatHelper.getPhoneTooltipWrapperText()}>
          <i className="fa fa-info-circle" />
        </TooltipWrapper>
      </div>
    </>;
  }

  private showWarningBanner = () => {
    return this.props.formState.reasonName === "Military Base" || this.props.formState.reasonName === "Provider Does not Contract with any MCO (Managed Care Organization)"
      || this.props.formState.reasonName === "Exclusive Agreement";
  }

  private canSubmit(): boolean {
    const editMode = this.props.projectionPanelMode === ProjectionPanelMode.Edit;
    const viewMode = this.props.projectionPanelMode === ProjectionPanelMode.View;
    const canSubmit = this.props.formState.status === ProjectionProviderStatus.CanSubmit;

    return !this.props.isRunning && ((editMode && this.props.formState.hasChanged) || (this.state && canSubmit && !viewMode));
  }

  private handleInternalOnlySubmit = () => {
    this.executeSubmit();
  };

  private handleRegulatorSubmitAttempt = () => {
    this.setState({ regulatorSubmitAttempted: true }, () => {
      if (!this.props.formState.reasonDescription?.length) {
        return;
      }
      this.setState({ showRegulatorWarningModal: true });
    });
  };

  private handleRegulatorWarningConfirm = () => {
    this.setState({ showRegulatorWarningModal: false }, () => this.executeSubmit());
  };

  private executeSubmit = async () => {
    if (this.canSubmit()) {
      // set the state to indicate we're beginning the 'create' process
      this.setState((preCreateState) => ({ ...preCreateState, status: ProjectionProviderStatus.Creating }), async () => {
        try {
          if (this.props.projectionPanelMode === ProjectionPanelMode.Edit) {
            this.props.providerNetworkType != ProviderNetworkTypeEnum.NetNew ?
              await this.projectService.updateProjectionProvider(this.props.projectId,
                {
                  allLocations: this.props.formState.allLocations,
                  allProjects: this.props.formState.allProjects,
                  allSpecialties: this.props.formState.allSpecialties,
                  exclusionVerifiedSources: this.props.formState.selectedExclusionVerifiedSources,
                  phone: this.phoneNumberFormatHelper.getNumbersFromString(this.props.formState.phone),
                  projectByNPI: this.props.formState.identifier.includes("npi"),
                  projectByTIN: this.props.formState.identifier.includes("tin"),
                  projectionProviderProjectId: this.props.projectionProvider.projectionProviderProjectId,
                  projectionProviderSettingsId: this.props.projectionProvider.projectionProviderSettingsId,
                  projectionProviderType: this.props.projectionProvider.projectionType,
                  providerNetworkType: this.props.providerNetworkType,
                  reasonDescription: this.props.formState.reasonDescription,
                  reasonName: this.props.formState.reasonName,
                  specialtyGroupCode: this.props.specialtyGroupCode,
                } as UpdateProjectionProvider) :
              await this.netNewServicingProviderService.updateNetNewReasonNameAndDescription(
                this.props.formState.reasonName,
                this.props.formState.reasonDescription,
                [this.props.projectionProvider.providerId]);

            this.props.formDispatch({ type: ProjectionProviderDetailsActionType.Finished });
            this.props.onProjectionProviderUpdated(this.state.runProjectionOnSubmit && this.props.formState.requireReRun, this.props.formState.requireReRun);
            this.props.clearWarnOnClose();
            this.props.handleCloseFlyout().then();
          } else {
            if (this.props.priorProjectionProviderType) {
              //changing projection provider type. Remove the old projection providers then
              //re-add with the new type
              // attempt to remove the projection providers
              await this.projectService.deleteProjectionProviders(this.props.projectId, this.props.priorProjectionProviderProjectIds);
            }
            const phone = this.props.accessPoints.length === 1 ? this.phoneNumberFormatHelper.getNumbersFromString(this.props.formState.phone) : null;

            const netNewProviders = this.props.accessPoints.filter(ap => ap.providerNetworkType === ProviderNetworkTypeEnum.NetNew);
            const impactProviders = this.props.accessPoints.filter(ap => ap.providerNetworkType !== ProviderNetworkTypeEnum.NetNew) || [];

            // get the first provider in the group
            if (impactProviders.length > 0) {
              const isNPI2 = this.props.formState.identifier === ProjectionProviderIdentifier.NPI2;
              await this.projectService.createProjectionProviders(
                this.props.projectId,
                impactProviders[0]?.providerNetworkType,
                this.props.projectionProviderType,
                !isNPI2 && this.props.formState.identifier.includes("npi"),
                this.props.formState.identifier.includes("tin"),
                this.props.formState.identifier.includes("contractentity"),
                isNPI2,
                impactProviders.map(x => x.providerId),
                this.props.formState.reasonName,
                this.props.formState.reasonDescription,
                this.props.formState.allSpecialties,
                this.props.formState.allLocations,
                this.props.formState.allProjects,
                phone,
                impactProviders[0].specialtyGroupCode,
                this.props.formState.selectedExclusionVerifiedSources,
                impactProviders
                  .filter(provider => !!provider.professionalAffiliationId)
                  .map((provider) => {
                    return {
                      contractEntityId: provider.professionalAffiliationId,
                      providerId: provider.providerId
                    }
                  }),
                null,
                false,
                this.props.formState.identifier === ProjectionProviderIdentifier.CCN,
                impactProviders.map(provider => provider.ccn).find(ccn => !!ccn) ?? ""
              );
            }

            if (netNewProviders.length > 0) {
              await this.netNewServicingProviderService.updateNetNewIsTargeted(true, netNewProviders.map(nnp => nnp.providerId));
              await this.netNewServicingProviderService.updateNetNewReasonNameAndDescription(this.props.formState.reasonName,
                this.props.formState.reasonDescription, netNewProviders.map(nnp => nnp.providerId));
            }

            // set the state to indicate we've successfully finished the 'create' process
            this.props.formDispatch({ type: ProjectionProviderDetailsActionType.Finished });
            // notify the consuming component that the projection providers have been created
            this.props.onProjectionProvidersCreated(this.state.runProjectionOnSubmit, undefined);
            this.props.clearWarnOnClose();
            this.props.handleCloseFlyout().then();
          }

        } catch (e) {
          // we failed to create the projection providers
          this.setState((prevState) => ({
            ...prevState,
            status: ProjectionProviderStatus.Error,
          }));
        }
      });
    }
  }

  private isProviderType(projectionProviderType: ProjectionProviderType): boolean {
    return this.props.projectionProviderType === projectionProviderType;
  }

  private get projectionTypeInfinitiveTense(): string {
    switch (this.props.projectionProviderType) {
      case ProjectionProviderType.ExcludeImpact:
        return "Exclude";
      case ProjectionProviderType.Target:
        return "Target";
      case ProjectionProviderType.Unknown:
        return "Unknown";
    }
  }

  private get projectionTypePresentTense(): string {
    switch (this.props.projectionProviderType) {
      case ProjectionProviderType.ExcludeImpact:
        return "Excluding";
      case ProjectionProviderType.Target:
        return "Targeting";
      case ProjectionProviderType.Unknown:
        return "Unknown";
    }
  }

  private get projectionTypeForNoun(): string {
    switch (this.props.projectionProviderType) {
      case ProjectionProviderType.ExcludeImpact:
        return "Exclusion";
      case ProjectionProviderType.Target:
        return "Target";
      case ProjectionProviderType.Unknown:
        return "Unknown";
    }
  }

  private get providerNetworkTypeDisplay(): string {
    switch (this.props.providerNetworkType) {
      case ProviderNetworkTypeEnum.Market:
        return "Market";
      case ProviderNetworkTypeEnum.Network:
        return "Network";
      case ProviderNetworkTypeEnum.Prospect:
        return "Prospect";
      default:
        return "";
    }
  }
}

const
  WrappedProjectionProviderDetails = withFeatureFlagContext(withFlyoutContext(withUserContext(ProjectionProviderDetails)));
export {
  WrappedProjectionProviderDetails
};
